// GstPayGSummary.js

import React, { useEffect, useMemo, useState } from "react";
import "./BASSummary.scss";
import axios from "axios";
import { baseUrl_SPB } from "../../../BaseUrl";

const PAGE_SIZE = 10;

const GstPayGSummary = ({ companyId, token }) => {

    const [activeTab, setActiveTab] = useState("gstReceivable");

    const [gstReceivable, setGstReceivable] = useState([]);
    const [gstPayable, setGstPayable] = useState([]);
    const [paygItems, setPaygItems] = useState([]);

    const [loading, setLoading] = useState(false);

    // =========================
    // PERIOD FILTERS
    // =========================
    const [periodType, setPeriodType] = useState("ALL");
    const [financialYear, setFinancialYear] = useState("ALL");

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [page, setPage] = useState(1);

    // =========================
    // LOAD DATA
    // =========================
    useEffect(() => {
        loadData();
    }, [companyId]);

    const loadData = async () => {
        try {
            setLoading(true);

            const headers = {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            };

            const [gstReceivableRes, gstPayableRes, paygRes] = await Promise.all([
                axios.get(
                    baseUrl_SPB +
                    `/api/v1/accounting/gstreceivable/gstreceivableentries?companyId=${companyId}`,
                    { headers }
                ),
                axios.get(
                    baseUrl_SPB +
                    `/api/v1/accounting/gstpayable/gstpayableentries?companyId=${companyId}`,
                    { headers }
                ),
                axios.get(
                    baseUrl_SPB +
                    `/api/v1/accounting/paygitem/paygitementries?companyId=${companyId}`,
                    { headers }
                )
            ]);

            setGstReceivable(gstReceivableRes.data);
            setGstPayable(gstPayableRes.data);
            setPaygItems(paygRes.data);

        } catch (e) {
            console.error("Error loading BAS data:", e);
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // AU FY LOGIC (Jul - Jun)
    // =========================
    const getFYFromDate = (date) => {
        const d = new Date(date);
        return d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
    };

    const getFinancialYears = (entries) => {
        if (!entries?.length) return ["ALL"];

        const years = new Set();

        entries.forEach(e => {
            if (!e.createdDate) return;
            years.add(getFYFromDate(e.createdDate));
        });

        const sorted = Array.from(years).sort((a, b) => b - a);

        const nextFY = Math.max(...sorted) + 1;

        return [
            "ALL",
            ...sorted.map(y => `FY${y}`),
            `FY${nextFY}`
        ];
    };

    // =========================
    // QUARTERS (AU FY)
    // =========================
    const getFinancialQuarterDates = (fy, quarter) => {

        const baseYear = fy;

        switch (quarter) {
            case "Q1":
                return { from: new Date(baseYear, 6, 1), to: new Date(baseYear, 8, 30) };

            case "Q2":
                return { from: new Date(baseYear, 9, 1), to: new Date(baseYear, 11, 31) };

            case "Q3":
                return { from: new Date(baseYear + 1, 0, 1), to: new Date(baseYear + 1, 2, 31) };

            case "Q4":
                return { from: new Date(baseYear + 1, 3, 1), to: new Date(baseYear + 1, 5, 30) };

            default:
                return null;
        }
    };

    // =========================
    // FILTER ENGINE
    // =========================
    const filterEntries = (entries) => {

        let start = null;
        let end = null;

        // =========================
        // FY FILTER
        // =========================
        if (financialYear !== "ALL") {
            const fy = parseInt(financialYear.replace("FY", ""));
            start = new Date(fy, 6, 1);
            end = new Date(fy + 1, 5, 30);
        }

        // =========================
        // QUARTER FILTER (overrides FY range)
        // =========================
        if (["Q1", "Q2", "Q3", "Q4"].includes(periodType)) {
            const fy = financialYear !== "ALL"
                ? parseInt(financialYear.replace("FY", ""))
                : getFYFromDate(new Date());

            const q = getFinancialQuarterDates(fy, periodType);
            start = q.from;
            end = q.to;
        }

        // =========================
        // CUSTOM RANGE
        // =========================
        if (periodType === "CUSTOM") {
            if (fromDate) start = new Date(fromDate);
            if (toDate) end = new Date(toDate);
        }

        return entries.filter((entry) => {
            if (!entry.createdDate) return false;

            const d = new Date(entry.createdDate);

            if (start && d < start) return false;
            if (end && d > end) return false;

            return true;
        });
    };

    // =========================
    // FILTERED DATA
    // =========================
    const filteredReceivable = useMemo(
        () => filterEntries(gstReceivable),
        [gstReceivable, periodType, financialYear, fromDate, toDate]
    );

    const filteredPayable = useMemo(
        () => filterEntries(gstPayable),
        [gstPayable, periodType, financialYear, fromDate, toDate]
    );

    const filteredPayg = useMemo(
        () => filterEntries(paygItems),
        [paygItems, periodType, financialYear, fromDate, toDate]
    );

    // =========================
    // TOTALS
    // =========================
    const sum = (items) =>
        items.reduce((s, i) => s + Number(i.amount || 0), 0);

    const gstReceivableTotal = sum(filteredReceivable);
    const gstPayableTotal = sum(filteredPayable);
    const paygTotal = sum(filteredPayg);

    const netBasPosition =
        gstPayableTotal - gstReceivableTotal + paygTotal;

    // =========================
    // TAB DATA
    // =========================
    const currentData = () => {
        switch (activeTab) {
            case "gstPayable":
                return filteredPayable;
            case "payg":
                return filteredPayg;
            default:
                return filteredReceivable;
        }
    };

    const data = currentData();

    const totalPages = Math.ceil(data.length / PAGE_SIZE);

    const pagedData = data.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    useEffect(() => {
        setPage(1);
    }, [activeTab, periodType, financialYear, fromDate, toDate]);

    // =========================
    // UI
    // =========================
    return (
        <div className="bas-summary">

            <div className="bas-header">
                <h2>BAS Summary</h2>
            </div>

            {/* =========================
                SUMMARY CARDS
            ========================= */}
            <div className="summary-cards">

                <div className="card">
                    <h4>GST Receivable</h4>
                    <span>${gstReceivableTotal.toFixed(2)}</span>
                </div>

                <div className="card">
                    <h4>GST Payable</h4>
                    <span>${gstPayableTotal.toFixed(2)}</span>
                </div>

                <div className="card">
                    <h4>PAYG Withholding</h4>
                    <span>${paygTotal.toFixed(2)}</span>
                </div>

                <div className="card highlight">
                    <h4>Net BAS Position</h4>
                    <span>${netBasPosition.toFixed(2)}</span>
                </div>

            </div>

            {/* =========================
                FILTERS
            ========================= */}
            <div className="filters">

                {/* FY DROPDOWN */}
                <select
                    value={financialYear}
                    onChange={(e) => setFinancialYear(e.target.value)}
                >
                    {getFinancialYears([
                        ...gstReceivable,
                        ...gstPayable,
                        ...paygItems
                    ]).map(fy => (
                        <option key={fy} value={fy}>
                            {fy === "ALL" ? "All FY" : fy}
                        </option>
                    ))}
                </select>

                {/* PERIOD */}
                <select
                    value={periodType}
                    onChange={(e) => setPeriodType(e.target.value)}
                >
                    <option value="ALL">All Transactions</option>
                    <option value="Q1">FY Q1 (Jul-Sep)</option>
                    <option value="Q2">FY Q2 (Oct-Dec)</option>
                    <option value="Q3">FY Q3 (Jan-Mar)</option>
                    <option value="Q4">FY Q4 (Apr-Jun)</option>
                    <option value="CUSTOM">Custom Date Range</option>
                </select>

                {periodType === "CUSTOM" && (
                    <>
                        <input type="date" value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)} />

                        <input type="date" value={toDate}
                            onChange={(e) => setToDate(e.target.value)} />
                    </>
                )}

            </div>

            {/* =========================
                TABS
            ========================= */}
            <div className="tabs">

                <button
                    className={activeTab === "gstReceivable" ? "active" : ""}
                    onClick={() => setActiveTab("gstReceivable")}
                >
                    GST Receivable
                </button>

                <button
                    className={activeTab === "gstPayable" ? "active" : ""}
                    onClick={() => setActiveTab("gstPayable")}
                >
                    GST Payable
                </button>

                <button
                    className={activeTab === "payg" ? "active" : ""}
                    onClick={() => setActiveTab("payg")}
                >
                    PAYG Withholding
                </button>

            </div>

            {/* =========================
                TABLE
            ========================= */}
            <table className="bas-table">

                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Transaction Id</th>
                        <th>Account</th>
                        <th>Account No</th>
                        <th>Amount</th>
                    </tr>
                </thead>

                <tbody>

                    {pagedData.map((item) => (
                        <tr key={item.id}>
                            <td>{item.createdDate}</td>
                            <td>{item.transactionId}</td>
                            <td>{item.accountName}</td>
                            <td>{item.accountNumber}</td>
                            <td>${Number(item.amount || 0).toFixed(2)}</td>
                        </tr>
                    ))}

                    {!loading && pagedData.length === 0 && (
                        <tr>
                            <td colSpan="5" style={{ textAlign: "center" }}>
                                No records found
                            </td>
                        </tr>
                    )}

                </tbody>

            </table>

            {/* =========================
                PAGINATION
            ========================= */}
            <div className="pagination">

                <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    Previous
                </button>

                <span>
                    Page {page} of {totalPages || 1}
                </span>

                <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                >
                    Next
                </button>

            </div>

        </div>
    );
};

export default GstPayGSummary;